import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Grid, Card, CardContent, Box, Button,
  Avatar, Chip, LinearProgress, IconButton, Badge, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Tabs, Tab, List, ListItem, ListItemIcon, ListItemText, Divider,
  Alert, CircularProgress, Accordion, AccordionSummary, AccordionDetails,
  Fab, Tooltip, Stack, CardActions, Rating
} from '@mui/material';
import {
  Dashboard, Assignment, Schedule, LocationOn, Star, TrendingUp,
  AttachMoney, Notifications, Settings, Visibility, CheckCircle,
  AccessTime, Navigation, Phone, Email, PhotoCamera, Videocam,
  Build, Assessment, Message, History, Person, RuleFolder,
  DirectionsCar, Timer, CameraAlt, Upload, FileDownload,
  ExpandMore, PlayArrow, Pause, Stop, CheckBox, Cancel,
  Warning, Info, Verified, Edit, Add, FilterList, Search,
  Route, MyLocation, Traffic, Speed, LocalShipping, School,
  Security, Engineering, ElectricalServices, Plumbing, LocalFireDepartment
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { brand, text, border } from '../../theme/colors';
import inspectorService from '../../services/inspectorService';
import DialogCloseButton from '../common/DialogCloseButton';

// Inspector Reports Tab Component
const InspectorReportsTab = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadReports = async () => {
      try {
        setLoading(true);
        const response = await inspectorService.getMyReports();
        if (response.success) {
          setReports(response.data || []);
        }
      } catch (err) {
        setError('Failed to load reports');
        console.error('Load reports error:', err);
      } finally {
        setLoading(false);
      }
    };
    loadReports();
  }, []);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress sx={{ color: brand.primary }} />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Inspection Reports</Typography>
              <Button variant="outlined" startIcon={<FilterList />} sx={{ borderColor: brand.primary, color: brand.primary, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}>
                Filter
              </Button>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {reports.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Assessment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No Reports Yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete inspections to generate reports.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {reports.map((report) => (
                  <Grid item xs={12} key={report.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Box>
                            <Typography variant="h6">{report.projectTitle}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Report #{report.reportNumber}
                            </Typography>
                          </Box>
                          <Chip 
                            label={report.result.toUpperCase()} 
                            color={report.result === 'pass' ? 'success' : report.result === 'fail' ? 'error' : 'warning'}
                          />
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary">Type</Typography>
                            <Typography variant="body2">{report.inspectionType}</Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary">Generated</Typography>
                            <Typography variant="body2">
                              {new Date(report.generatedAt).toLocaleDateString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                            <Typography variant="body2">{report.status}</Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary">Actions</Typography>
                            <Box>
                              <Button size="small" startIcon={<FileDownload />}>
                                Download
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const QualityInspectorDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [inspectionDetailsDialog, setInspectionDetailsDialog] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [startInspectionDialog, setStartInspectionDialog] = useState(false);
  const [availabilityDialog, setAvailabilityDialog] = useState(false);
  
  // Data states
  const [inspectorProfile, setInspectorProfile] = useState({});
  const [activeInspections, setActiveInspections] = useState([]);
  const [upcomingInspections, setUpcomingInspections] = useState([]);
  const [stats, setStats] = useState({});
  const [certifications, setCertifications] = useState([]);

  // Determine inspector tier and specialties
  const inspectorTier = user?.inspector_info?.tier || 'certified_inspector';
  const isLeadInspector = inspectorTier === 'lead_inspector';

  // Sample comprehensive data for quality inspector
  const sampleData = {
    profile: {
      id: 'QI-001',
      name: (user?.firstName && user?.lastName) 
        ? `${user.firstName} ${user.lastName}` 
        : user?.firstName || user?.lastName || 'Professional Quality Inspector',
      tier: inspectorTier,
      location: 'Harare, Zimbabwe',
      avatar: null,
      rating: 4.9,
      reviewCount: 47,
      totalInspections: 156,
      responseTime: '12 mins avg',
      availability: 'available',
      specializations: ['Structural Engineering', 'Electrical Systems', 'Fire Safety', 'Building Codes'],
      certifications: ['Professional Engineer', 'Building Inspector License', 'Fire Safety Certification', 'Electrical Inspector']
    },
    stats: {
      todayInspections: 2,
      pendingInspections: 5,
      completedThisWeek: 8,
      completedThisMonth: 24,
      monthlyEarnings: 4200,
      averageRating: 4.9,
      responseTime: 12,
      completionRate: 97,
      passRate: 89,
      criticalFindings: 3
    },
    activeInspections: [
      {
        id: 'INSP-001',
        projectId: 'PROJ-2001',
        projectTitle: 'Office Complex - Phase 2',
        inspectionType: 'structural',
        phase: 'Foundation & Framework',
        client: 'Corporate Developments Ltd',
        contractor: 'Premier Construction Group',
        location: {
          address: '250 Nelson Mandela Ave, Harare',
          coordinates: { lat: -17.8292, lng: 31.0522 },
          distance: 15.2,
          area: 'CBD'
        },
        scheduledDate: '2025-02-15',
        scheduledTime: '09:30',
        duration: 180,
        priority: 'high',
        status: 'in_progress',
        paymentAmount: 350,
        description: 'Comprehensive structural integrity inspection of concrete foundation and steel framework',
        requirements: [
          'Foundation depth and strength verification',
          'Concrete compression test results review',
          'Steel framework alignment and welding quality',
          'Load-bearing calculations verification',
          'Safety compliance assessment'
        ],
        checklist: [
          { item: 'Foundation depth measurement', status: 'completed', critical: true },
          { item: 'Concrete strength verification', status: 'completed', critical: true },
          { item: 'Reinforcement placement check', status: 'in_progress', critical: true },
          { item: 'Steel framework alignment', status: 'pending', critical: false },
          { item: 'Welding quality assessment', status: 'pending', critical: true },
          { item: 'Safety compliance review', status: 'pending', critical: false }
        ],
        documents: ['Structural drawings', 'Engineering calculations', 'Material certificates'],
        clientContact: '+263771234567',
        contractorContact: '+263771234568',
        estimatedCompletion: '2025-02-15 12:30',
        riskLevel: 'medium'
      },
      {
        id: 'INSP-002',
        projectId: 'PROJ-2002',
        projectTitle: 'Shopping Mall Electrical Systems',
        inspectionType: 'electrical',
        phase: 'Final Electrical Installation',
        client: 'Retail Development Corporation',
        contractor: 'ElectroTech Solutions',
        location: {
          address: '89 Borrowdale Road, Harare',
          coordinates: { lat: -17.8175, lng: 31.0467 },
          distance: 8.7,
          area: 'Borrowdale'
        },
        scheduledDate: '2025-02-15',
        scheduledTime: '14:00',
        duration: 150,
        priority: 'urgent',
        status: 'assigned',
        paymentAmount: 280,
        description: 'Final electrical systems inspection before commissioning for large retail complex',
        requirements: [
          'Main electrical panel inspection',
          'Emergency lighting systems test',
          'Fire alarm integration verification',
          'Power distribution verification',
          'Grounding system compliance'
        ],
        checklist: [
          { item: 'Main panel configuration', status: 'pending', critical: true },
          { item: 'Circuit breaker ratings', status: 'pending', critical: true },
          { item: 'Emergency lighting test', status: 'pending', critical: true },
          { item: 'Fire alarm systems', status: 'pending', critical: true },
          { item: 'Grounding verification', status: 'pending', critical: true },
          { item: 'Load testing', status: 'pending', critical: false }
        ],
        documents: ['Electrical schematics', 'Panel schedules', 'Test certificates'],
        clientContact: '+263771234569',
        contractorContact: '+263771234570',
        estimatedCompletion: '2025-02-15 16:30',
        riskLevel: 'high'
      }
    ],
    upcomingInspections: [
      {
        id: 'INSP-003',
        projectTitle: 'Residential Complex - Fire Safety',
        inspectionType: 'fire_safety',
        client: 'Housing Development Co',
        scheduledDate: '2025-02-16',
        scheduledTime: '10:00',
        location: { address: 'Westgate, Harare', distance: 12.5 },
        priority: 'normal',
        paymentAmount: 220
      },
      {
        id: 'INSP-004',
        projectTitle: 'Industrial Warehouse - Structural',
        inspectionType: 'structural',
        client: 'Industrial Holdings Ltd',
        scheduledDate: '2025-02-17',
        scheduledTime: '08:00',
        location: { address: 'Msasa Industrial, Harare', distance: 25.3 },
        priority: 'high',
        paymentAmount: 400
      },
      {
        id: 'INSP-005',
        projectTitle: 'School Building - Accessibility',
        inspectionType: 'accessibility',
        client: 'Education Ministry',
        scheduledDate: '2025-02-18',
        scheduledTime: '11:30',
        location: { address: 'Mbare, Harare', distance: 18.2 },
        priority: 'normal',
        paymentAmount: 180
      }
    ],
    certifications: [
      {
        id: 'CERT-001',
        name: 'Professional Engineer',
        issuer: 'Zimbabwe Institution of Engineers',
        level: 'professional',
        specialty: 'structural',
        issuedDate: '2020-03-15',
        expiryDate: '2025-03-15',
        status: 'active',
        verified: true
      },
      {
        id: 'CERT-002',
        name: 'Building Inspector License',
        issuer: 'Ministry of Public Works',
        level: 'advanced',
        specialty: 'general',
        issuedDate: '2019-08-20',
        expiryDate: '2024-08-20',
        status: 'expired',
        verified: true
      },
      {
        id: 'CERT-003',
        name: 'Fire Safety Inspector',
        issuer: 'Fire Brigade Association',
        level: 'specialist',
        specialty: 'fire_safety',
        issuedDate: '2021-01-10',
        expiryDate: '2026-01-10',
        status: 'active',
        verified: true
      },
      {
        id: 'CERT-004',
        name: 'Electrical Inspector Certification',
        issuer: 'Electrical Contractors Association',
        level: 'advanced',
        specialty: 'electrical',
        issuedDate: '2020-11-05',
        expiryDate: '2025-11-05',
        status: 'active',
        verified: true
      }
    ]
  };

  // Load inspector data
  useEffect(() => {
    const loadInspectorData = async () => {
      try {
        setLoading(true);
        console.log('Loading inspector data for user:', user?.id);
        
        // Load data from APIs
        const [statsRes, inspectionsRes, certificationsRes] = await Promise.allSettled([
          inspectorService.getMyStats(),
          inspectorService.getMyInspections(),
          inspectorService.getMyCertifications()
        ]);

        // Set stats
        if (statsRes.status === 'fulfilled' && statsRes.value.success) {
          setStats(statsRes.value.data);
        } else {
          console.warn('Failed to load stats, using sample data');
          setStats(sampleData.stats);
        }

        // Set inspections
        if (inspectionsRes.status === 'fulfilled' && inspectionsRes.value.success) {
          const inspections = inspectionsRes.value.data || [];
          setActiveInspections(inspections.filter(i => 
            ['assigned', 'in_progress'].includes(i.status)
          ));
          setUpcomingInspections(inspections.filter(i => i.status === 'scheduled'));
        } else {
          console.warn('Failed to load inspections, using sample data');
          setActiveInspections(sampleData.activeInspections);
          setUpcomingInspections(sampleData.upcomingInspections);
        }

        // Set certifications
        if (certificationsRes.status === 'fulfilled' && certificationsRes.value.success) {
          setCertifications(certificationsRes.value.data || []);
        } else {
          console.warn('Failed to load certifications, using sample data');
          setCertifications(sampleData.certifications);
        }

        // Set inspector profile from user context
        setInspectorProfile({
          ...sampleData.profile,
          name: (user?.firstName && user?.lastName) 
            ? `${user.firstName} ${user.lastName}` 
            : user?.firstName || user?.lastName || 'Professional Quality Inspector',
          id: user?.id || 'QI-001'
        });
        
      } catch (err) {
        setError('Failed to load inspector data');
        console.error('Error loading inspector data:', err);
        // Fall back to sample data
        setInspectorProfile(sampleData.profile);
        setActiveInspections(sampleData.activeInspections);
        setUpcomingInspections(sampleData.upcomingInspections);
        setStats(sampleData.stats);
        setCertifications(sampleData.certifications);
      } finally {
        setLoading(false);
      }
    };

    loadInspectorData();
  }, [user?.id, user?.firstName, user?.lastName]);

  const getStatusColor = (status) => {
    const colors = {
      'assigned': 'primary',
      'in_progress': 'warning',
      'completed': 'success',
      'pending': 'info',
      'failed': 'error',
      'passed': 'success'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': 'error',
      'high': 'warning',
      'normal': 'primary',
      'low': 'info'
    };
    return colors[priority] || 'default';
  };

  const getRiskLevelColor = (risk) => {
    const colors = {
      'high': 'error',
      'medium': 'warning',
      'low': 'success'
    };
    return colors[risk] || 'default';
  };

  const getCertificationStatusColor = (status) => {
    const colors = {
      'active': 'success',
      'expired': 'error',
      'expiring': 'warning'
    };
    return colors[status] || 'default';
  };

  const getInspectionTypeIcon = (type) => {
    const icons = {
      'structural': <Engineering />,
      'electrical': <ElectricalServices />,
      'fire_safety': <LocalFireDepartment />,
      'plumbing': <Plumbing />,
      'accessibility': <Person />,
      'general': <Assignment />
    };
    return icons[type] || <Assignment />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleStartInspection = async (inspection) => {
    try {
      if (!inspection) {
        setError('No inspection selected');
        return;
      }
      setSelectedInspection(inspection);
      setStartInspectionDialog(true);
    } catch (err) {
      setError('Failed to open inspection dialog');
      console.error('Start inspection error:', err);
    }
  };

  const confirmStartInspection = async () => {
    try {
      if (!selectedInspection) {
        setError('No inspection selected');
        return;
      }
      
      const response = await inspectorService.startInspection(selectedInspection.id);
      
      if (response.success) {
        setSuccess('Inspection started successfully');
        setStartInspectionDialog(false);
        setSelectedInspection(null);
        
        // Reload inspections
        const inspectionsRes = await inspectorService.getMyInspections();
        if (inspectionsRes.success) {
          const inspections = inspectionsRes.data || [];
          setActiveInspections(inspections.filter(i => 
            ['assigned', 'in_progress'].includes(i.status)
          ));
        }
      } else {
        setError(response.message || 'Failed to start inspection');
      }
    } catch (err) {
      setError('Failed to start inspection: ' + (err.message || 'Unknown error'));
      console.error('Start inspection error:', err);
    }
  };

  const handleGetDirections = (inspection) => {
    if (!inspection || !inspection.location) {
      setError('Location information not available');
      return;
    }
    
    const { coordinates, address } = inspection.location;
    let url;
    
    if (coordinates && coordinates.lat && coordinates.lng) {
      // Use coordinates if available
      url = `https://www.google.com/maps/dir/?api=1&destination=${coordinates.lat},${coordinates.lng}`;
    } else if (address) {
      // Fallback to address
      url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    } else {
      setError('Location coordinates not available');
      return;
    }
    
    window.open(url, '_blank');
  };

  const handleContactClient = (inspection) => {
    if (!inspection) {
      setError('Inspection information not available');
      return;
    }
    
    const phoneNumber = inspection.clientContact || inspection.contractorContact;
    
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    } else {
      setError('Contact information not available');
    }
  };

  const [availabilityStatus, setAvailabilityStatus] = useState('available');
  const [maxDailyInspections, setMaxDailyInspections] = useState(3);
  const [acceptUrgent, setAcceptUrgent] = useState(true);
  const [travelOutside, setTravelOutside] = useState(true);

  const handleSaveAvailability = async () => {
    try {
      const isAvailable = availabilityStatus === 'available';
      const response = await inspectorService.updateAvailability(isAvailable);
      
      if (response.success) {
        setSuccess('Availability settings updated successfully');
        setAvailabilityDialog(false);
        
        // Update profile
        setInspectorProfile(prev => ({
          ...prev,
          availability: availabilityStatus
        }));
      } else {
        setError(response.message || 'Failed to update availability');
      }
    } catch (err) {
      setError('Failed to update availability: ' + (err.message || 'Unknown error'));
      console.error('Update availability error:', err);
    }
  };

  const handleViewInspectionDetails = (inspection) => {
    setSelectedInspection(inspection);
    setInspectionDetailsDialog(true);
  };

  const calculateChecklistProgress = (checklist) => {
    if (!checklist || checklist.length === 0) return 0;
    const completed = checklist.filter(item => item.status === 'completed').length;
    return (completed / checklist.length) * 100;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} sx={{ color: brand.primary }} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading inspector dashboard...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar 
            sx={{ width: 60, height: 60 }}
            src={inspectorProfile.avatar}
          >
            {inspectorProfile.name?.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="h4">
              Welcome, {inspectorProfile.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Chip 
                icon={<Verified />}
                label={isLeadInspector ? 'Lead Inspector' : 'Certified Quality Inspector'}
                color={isLeadInspector ? 'primary' : 'success'}
                size="small"
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Star sx={{ color: 'gold', fontSize: 20 }} />
                <Typography variant="body2">
                  {inspectorProfile.rating} ({inspectorProfile.reviewCount} reviews)
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton>
            <Badge badgeContent={stats.pendingInspections} color="error">
              <Notifications />
            </Badge>
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => setAvailabilityDialog(true)}
            sx={{ borderColor: brand.primary, color: brand.primary, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}
          >
            Settings
          </Button>
          <Chip
            icon={<CheckCircle />}
            label="Available"
            variant="outlined"
            sx={{ borderColor: '#16a085', color: '#16a085' }}
          />
        </Box>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `2px solid ${border.light}` }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ color: '#43B581', fontWeight: 700 }}>
                {stats.todayInspections}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Today's Inspections
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
                <Schedule fontSize="small" />
                <Typography variant="caption">
                  Next at 9:30 AM
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `2px solid ${border.light}` }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ color: '#43B581', fontWeight: 700 }}>
                {stats.completedThisWeek}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed This Week
              </Typography>
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={80}
                  sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: '#43B581' }, backgroundColor: border.light }}
                />
                <Typography variant="caption" color="text.secondary">
                  80% of weekly target
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `2px solid ${border.light}` }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ color: '#43B581', fontWeight: 700 }}>
                {formatCurrency(stats.monthlyEarnings)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This Month's Earnings
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  This month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `2px solid ${border.light}` }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ color: '#43B581', fontWeight: 700 }}>
                {stats.passRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pass Rate
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
                <CheckCircle fontSize="small" />
                <Typography variant="caption">
                  {stats.completionRate}% completion rate
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ '& .MuiTabs-indicator': { backgroundColor: brand.primary }, '& .MuiTab-root.Mui-selected': { color: brand.primary, fontWeight: 600 }, '& .MuiTab-root': { color: text.secondary } }}>
          <Tab icon={<Dashboard />} label="Overview" />
          <Tab icon={<Assignment />} label="Active Inspections" />
          <Tab icon={<Schedule />} label="Upcoming" />
          <Tab icon={<Verified />} label="Certifications" />
          {isLeadInspector && <Tab icon={<Assessment />} label="Quality Control" />}
          <Tab icon={<History />} label="Reports" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Active Inspections Overview */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Active Inspections</Typography>
                  <Button size="small" startIcon={<Add />}>
                    Request Assignment
                  </Button>
                </Box>
                
                {activeInspections.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No Active Inspections
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Check upcoming inspections or request new assignments.
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {activeInspections.map((inspection) => (
                      <Grid item xs={12} key={inspection.id}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {getInspectionTypeIcon(inspection.inspectionType)}
                                <Box>
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {inspection.inspectionType.replace('_', ' ').toUpperCase()} Inspection
                                  </Typography>
                                  <Typography variant="body1" gutterBottom>
                                    {inspection.projectTitle}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip 
                                  label={inspection.status.replace('_', ' ').toUpperCase()} 
                                  color={getStatusColor(inspection.status)}
                                  size="small"
                                />
                                <Chip 
                                  label={inspection.priority.toUpperCase()} 
                                  color={getPriorityColor(inspection.priority)}
                                  size="small"
                                />
                              </Box>
                            </Box>
                            
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary">Client</Typography>
                                <Typography variant="body2">{inspection.client}</Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary">Scheduled</Typography>
                                <Typography variant="body2">
                                  {inspection.scheduledDate} at {inspection.scheduledTime}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary">Duration</Typography>
                                <Typography variant="body2">{inspection.duration} mins</Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary">Payment</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#43B581' }}>
                                  {formatCurrency(inspection.paymentAmount)}
                                </Typography>
                              </Grid>
                            </Grid>

                            {inspection.checklist && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Progress: {Math.round(calculateChecklistProgress(inspection.checklist))}%
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={calculateChecklistProgress(inspection.checklist)}
                                  sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: '#43B581' }, backgroundColor: border.light }}
                                />
                              </Box>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationOn fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {inspection.location.area} • {inspection.location.distance}km
                                </Typography>
                              </Box>
                              <Chip 
                                label={`${inspection.riskLevel} risk`.toUpperCase()} 
                                color={getRiskLevelColor(inspection.riskLevel)}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          </CardContent>
                          <CardActions>
                            <Button 
                              size="small"
                              onClick={() => handleViewInspectionDetails(inspection)}
                            >
                              View Details
                            </Button>
                            <Button 
                              size="small" 
                              startIcon={<Route />}
                              onClick={() => handleGetDirections(inspection)}
                            >
                              Directions
                            </Button>
                            <Button 
                              size="small" 
                              startIcon={<Phone />}
                              onClick={() => handleContactClient(inspection)}
                            >
                              Contact
                            </Button>
                            {inspection.status === 'assigned' && (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<PlayArrow />}
                                sx={{ backgroundColor: '#43B581', '&:hover': { backgroundColor: '#1a8a6e' } }}
                                onClick={() => handleStartInspection(inspection)}
                              >
                                Start
                              </Button>
                            )}
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Upcoming Inspections */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Upcoming Inspections
                </Typography>
                {upcomingInspections.slice(0, 3).map((inspection) => (
                  <Box key={inspection.id} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {inspection.projectTitle}
                      </Typography>
                      <Chip 
                        label={inspection.priority.toUpperCase()} 
                        color={getPriorityColor(inspection.priority)}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {inspection.inspectionType.replace('_', ' ').toUpperCase()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(inspection.scheduledDate)} at {inspection.scheduledTime}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#43B581' }}>
                      {formatCurrency(inspection.paymentAmount)}
                    </Typography>
                  </Box>
                ))}
                <Button size="small" fullWidth sx={{ mt: 1 }}>
                  View All Upcoming
                </Button>
              </CardContent>
            </Card>

            {/* Certifications Status */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Certification Status
                </Typography>
                {certifications.slice(0, 4).map((cert) => (
                  <Box key={cert.id} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {cert.name}
                      </Typography>
                      <Chip 
                        label={cert.status.toUpperCase()} 
                        color={getCertificationStatusColor(cert.status)}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Expires: {formatDate(cert.expiryDate)}
                    </Typography>
                    {cert.verified && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Verified fontSize="small" sx={{ color: '#16a085' }} />
                        <Typography variant="caption" sx={{ color: '#16a085' }}>
                          Verified
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
                <Button size="small" fullWidth sx={{ mt: 1 }}>
                  Manage Certifications
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<CameraAlt />}
                      sx={{ py: 2, borderColor: border.light, color: text.primary, '&:hover': { borderColor: '#43B581', backgroundColor: 'rgba(67,181,129,0.04)' } }}
                    >
                      Start Inspection
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Schedule />}
                      sx={{ py: 2, borderColor: border.light, color: text.primary, '&:hover': { borderColor: '#43B581', backgroundColor: 'rgba(67,181,129,0.04)' } }}
                    >
                      View Schedule
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<FileDownload />}
                      sx={{ py: 2, borderColor: border.light, color: text.primary, '&:hover': { borderColor: '#43B581', backgroundColor: 'rgba(67,181,129,0.04)' } }}
                    >
                      Generate Report
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Assessment />}
                      sx={{ py: 2, borderColor: border.light, color: text.primary, '&:hover': { borderColor: '#43B581', backgroundColor: 'rgba(67,181,129,0.04)' } }}
                    >
                      Quality Tools
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Active Inspections Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Active Inspections</Typography>
                {activeInspections.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No Active Inspections
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      All inspections are completed or scheduled for future dates.
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {activeInspections.map((inspection) => (
                      <Grid item xs={12} key={inspection.id}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="h6">{inspection.projectTitle}</Typography>
                              <Chip 
                                label={inspection.status.toUpperCase()} 
                                color={getStatusColor(inspection.status)}
                              />
                            </Box>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Client</Typography>
                                <Typography variant="body2">{inspection.client}</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Type</Typography>
                                <Typography variant="body2">{inspection.inspectionType}</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Scheduled</Typography>
                                <Typography variant="body2">{inspection.scheduledDate}</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Payment</Typography>
                                <Typography variant="body2" sx={{ color: '#43B581' }}>
                                  {formatCurrency(inspection.paymentAmount)}
                                </Typography>
                              </Grid>
                            </Grid>
                          </CardContent>
                          <CardActions>
                            <Button size="small" onClick={() => handleViewInspectionDetails(inspection)}>
                              View Details
                            </Button>
                            {inspection.status === 'assigned' && (
                              <Button
                                size="small"
                                variant="contained"
                                sx={{ backgroundColor: '#43B581', '&:hover': { backgroundColor: '#1a8a6e' } }}
                                onClick={() => handleStartInspection(inspection)}
                              >
                                Start
                              </Button>
                            )}
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Upcoming Inspections Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Upcoming Inspections</Typography>
                <Grid container spacing={2}>
                  {upcomingInspections.map((inspection) => (
                    <Grid item xs={12} md={6} key={inspection.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6">{inspection.projectTitle}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {inspection.inspectionType}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {formatDate(inspection.scheduledDate)} at {inspection.scheduledTime}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: '#43B581' }}>
                            {formatCurrency(inspection.paymentAmount)}
                          </Typography>
                          <Chip 
                            label={inspection.priority.toUpperCase()} 
                            color={getPriorityColor(inspection.priority)}
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Certifications Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">My Certifications</Typography>
                  <Button variant="contained" startIcon={<Add />} sx={{ backgroundColor: '#43B581', '&:hover': { backgroundColor: '#1a8a6e' } }}>
                    Add Certification
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  {certifications.map((cert) => (
                    <Grid item xs={12} md={6} key={cert.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6">{cert.name}</Typography>
                            <Chip 
                              label={cert.status.toUpperCase()} 
                              color={getCertificationStatusColor(cert.status)}
                              size="small"
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {cert.issuer}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Level:</strong> {cert.level}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Specialty:</strong> {cert.specialty}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Issued:</strong> {formatDate(cert.issuedDate)}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Expires:</strong> {formatDate(cert.expiryDate)}
                          </Typography>
                          {cert.verified && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                              <Verified fontSize="small" sx={{ color: '#16a085' }} />
                              <Typography variant="caption" sx={{ color: '#16a085' }}>
                                Verified
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Quality Control Tab (Lead Inspector only) */}
      {activeTab === 4 && isLeadInspector && (
        <Alert severity="info">
          Advanced quality control tools and team management features will be built here.
        </Alert>
      )}

      {/* Reports Tab */}
      {activeTab === 5 && (
        <InspectorReportsTab />
      )}

      {/* Inspection Details Dialog */}
      <Dialog
        open={inspectionDetailsDialog}
        onClose={() => setInspectionDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setInspectionDetailsDialog(false)} />
        <DialogTitle>Inspection Details</DialogTitle>
        <DialogContent dividers>
          {selectedInspection && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedInspection.inspectionType.replace('_', ' ').toUpperCase()} Inspection
              </Typography>
              <Typography variant="body1" gutterBottom>
                {selectedInspection.projectTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {selectedInspection.description}
              </Typography>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1">Inspection Checklist</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {selectedInspection.checklist?.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          {item.critical ? <Warning sx={{ color: '#e74c3c' }} /> : <CheckBox />}
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.item}
                          secondary={item.critical ? 'Critical Item' : 'Standard Check'}
                        />
                        <Chip 
                          label={item.status.toUpperCase()} 
                          color={getStatusColor(item.status)}
                          size="small"
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1">Requirements & Documents</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="subtitle2" gutterBottom>Requirements:</Typography>
                  <List dense>
                    {selectedInspection.requirements?.map((req, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckBox />
                        </ListItemIcon>
                        <ListItemText primary={req} />
                      </ListItem>
                    ))}
                  </List>
                  
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Documents:</Typography>
                  <List dense>
                    {selectedInspection.documents?.map((doc, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <FileDownload />
                        </ListItemIcon>
                        <ListItemText primary={doc} />
                        <Button size="small" startIcon={<FileDownload />}>
                          Download
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInspectionDetailsDialog(false)}>Close</Button>
          <Button 
            variant="outlined" 
            startIcon={<Route />}
            onClick={() => {
              handleGetDirections(selectedInspection);
              setInspectionDetailsDialog(false);
            }}
          >
            Get Directions
          </Button>
          {selectedInspection && selectedInspection.status === 'assigned' && (
            <Button
              variant="contained"
              startIcon={<PlayArrow />}
              sx={{ backgroundColor: '#43B581', '&:hover': { backgroundColor: '#1a8a6e' } }}
              onClick={() => {
                setInspectionDetailsDialog(false);
                handleStartInspection(selectedInspection);
              }}
            >
              Start Inspection
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Start Inspection Dialog */}
      <Dialog
        open={startInspectionDialog}
        onClose={() => setStartInspectionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => setStartInspectionDialog(false)} />
        <DialogTitle>Start Quality Inspection</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Ready to start inspection for:
          </Typography>
          <Typography variant="h6" gutterBottom>
            {selectedInspection?.inspectionType?.replace('_', ' ').toUpperCase()} Inspection
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {selectedInspection?.projectTitle}
          </Typography>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            Ensure you have all required equipment, documentation, and safety gear before starting.
          </Alert>
          
          <Alert severity="warning" sx={{ mt: 1 }}>
            Location: {selectedInspection?.location?.address}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartInspectionDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            sx={{ backgroundColor: '#43B581', '&:hover': { backgroundColor: '#1a8a6e' } }}
            onClick={confirmStartInspection}
          >
            Start Inspection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Availability Settings Dialog */}
      <Dialog
        open={availabilityDialog}
        onClose={() => setAvailabilityDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => setAvailabilityDialog(false)} />
        <DialogTitle>Inspector Settings</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 3, mt: 2 }}>
            <InputLabel>Availability Status</InputLabel>
            <Select 
              value={availabilityStatus} 
              onChange={(e) => setAvailabilityStatus(e.target.value)}
              label="Availability Status"
            >
              <MenuItem value="available">Available</MenuItem>
              <MenuItem value="busy">Busy</MenuItem>
              <MenuItem value="offline">Offline</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            type="number"
            label="Max Daily Inspections"
            value={maxDailyInspections}
            onChange={(e) => setMaxDailyInspections(parseInt(e.target.value))}
            sx={{ mb: 2 }}
            inputProps={{ min: 1, max: 10 }}
          />
          
          <FormControlLabel
            control={
              <Switch 
                checked={acceptUrgent}
                onChange={(e) => setAcceptUrgent(e.target.checked)}
              />
            }
            label="Accept urgent inspections"
          />
          
          <FormControlLabel
            control={
              <Switch 
                checked={travelOutside}
                onChange={(e) => setTravelOutside(e.target.checked)}
              />
            }
            label="Travel outside service area"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvailabilityDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#43B581', '&:hover': { backgroundColor: '#1a8a6e' } }}
            onClick={handleSaveAvailability}
          >
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for Quick Inspection */}
      {activeInspections.length > 0 && activeInspections[0] && (
        <Fab
          sx={{ position: 'fixed', bottom: 16, right: 16, backgroundColor: '#43B581', color: 'white', '&:hover': { backgroundColor: '#1a8a6e' } }}
          onClick={() => handleStartInspection(activeInspections[0])}
        >
          <CameraAlt />
        </Fab>
      )}
    </Container>
  );
};

export default QualityInspectorDashboard;